"use client";

import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";

const ONBOARDING_STORAGE_KEY = "projectmed_onboarding_step";

const STEP_MESSAGES: Record<
  number,
  { title: string; desc: string; next: string; nextPath: string; nextStep: number | null }
> = {
  2: {
    title: "Etapa 3 de 5 — Disponibilidade",
    desc: "Defina seus dias e horários de atendimento. Quando terminar, clique abaixo para continuar a configuração.",
    next: "Concluído! Ir para Tipos de Serviço →",
    nextPath: "/event-types",
    nextStep: 3,
  },
  3: {
    title: "Etapa 4 de 5 — Tipos de Serviço",
    desc: "Crie pelo menos um tipo de serviço (ex: Diagnóstico, Troca de óleo, Revisão preventiva). Quando terminar, clique abaixo.",
    next: "Concluído! Ir para Agente IA →",
    nextPath: "/agente-ia",
    nextStep: 4,
  },
  4: {
    title: "Etapa 5 de 5 — Agente IA",
    desc: "Configure sua assistente virtual de WhatsApp. Quando terminar, clique abaixo para concluir.",
    next: "Concluir configuração ✓",
    nextPath: "/dashboard",
    nextStep: null,
  },
  // Fluxo curto da conta só-CRM (o wizard grava 5 ao ir para /whatsapp).
  5: {
    title: "Etapa 3 de 3 — WhatsApp",
    desc: "Escaneie o QR Code para conectar o número da oficina ao CRM. Quando conectar, clique abaixo para concluir.",
    next: "Concluir configuração ✓",
    nextPath: "/dashboard",
    nextStep: null,
  },
};

export function setOnboardingStep(step: number) {
  if (typeof window !== "undefined") {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, String(step));
    window.dispatchEvent(new Event("onboarding-changed"));
  }
}

export function clearOnboarding() {
  if (typeof window !== "undefined") {
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
    window.dispatchEvent(new Event("onboarding-changed"));
  }
}

export default function OnboardingFloatingWidget() {
  const pathname = usePathname();
  const router = useRouter();
  const [step, setStep] = useState<number | null>(null);
  const [minimized, setMinimized] = useState(false);

  // Load and listen for changes — multiple listeners for resilience across navigations/reloads
  useEffect(() => {
    const load = () => {
      const stored = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      setStep(stored ? parseInt(stored, 10) : null);
    };
    load();
    window.addEventListener("onboarding-changed", load);
    window.addEventListener("storage", load);
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", load);

    // Periodic check every 2s as fallback for edge cases (page transitions, etc.)
    const interval = setInterval(load, 2000);

    return () => {
      window.removeEventListener("onboarding-changed", load);
      window.removeEventListener("storage", load);
      window.removeEventListener("focus", load);
      document.removeEventListener("visibilitychange", load);
      clearInterval(interval);
    };
  }, []);

  // Don't show on dados-principais page (it has its own UI)
  // Don't show if no active step
  if (step === null || !STEP_MESSAGES[step]) return null;
  if (pathname?.startsWith("/dados-principais")) return null;

  const msg = STEP_MESSAGES[step];

  const handleNext = () => {
    if (msg.nextStep !== null) {
      setOnboardingStep(msg.nextStep);
      router.push(msg.nextPath);
    } else {
      // Done!
      clearOnboarding();
      router.push(msg.nextPath);
    }
  };

  const handleBack = () => {
    router.push("/dados-principais");
    clearOnboarding();
  };

  const handleDismiss = () => {
    clearOnboarding();
  };

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-brand-deep to-brand text-white shadow-xl shadow-[#063D56]/30 transition-all hover:scale-110 active:scale-95"
        style={{ animation: "bounceIn 0.4s ease-out" }}>
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l7.5-7.5 7.5 7.5" />
        </svg>
        <style>{`
          @keyframes bounceIn { 0% { transform: scale(0); } 60% { transform: scale(1.1); } 100% { transform: scale(1); } }
        `}</style>
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-6 right-6 z-50 w-80 overflow-hidden rounded-2xl bg-white shadow-2xl shadow-[#063D56]/15"
      style={{ animation: "slideUp 0.4s ease-out", border: "1px solid rgba(0,0,0,0.08)" }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-deep to-brand px-5 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-white animate-pulse" />
            <span className="text-sm font-semibold text-white">{msg.title}</span>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setMinimized(true)}
              className="rounded-lg p-1 text-white/70 hover:bg-white/20 hover:text-white transition-colors"
              title="Minimizar">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <button
              onClick={handleDismiss}
              className="rounded-lg p-1 text-white/70 hover:bg-white/20 hover:text-white transition-colors"
              title="Fechar">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        <p className="text-sm text-gray-600 leading-relaxed">{msg.desc}</p>

        {/* Step dots (o fluxo só-CRM tem uma etapa guiada só — sem trilha de dots) */}
        <div className="mt-4 mb-4 flex items-center justify-center gap-2">
          {(step === 5 ? [5] : [2, 3, 4]).map((s) => (
            <div
              key={s}
              className={`h-2 rounded-full transition-all duration-300 ${
                s === step ? "w-6 bg-[#075F78]" : s < step ? "w-2 bg-[#8FC8D6]" : "w-2 bg-gray-200"
              }`}
            />
          ))}
        </div>

        <button
          onClick={handleNext}
          className="w-full rounded-xl bg-gradient-to-r from-brand-deep to-brand px-4 py-2.5 text-sm font-medium text-white shadow-lg shadow-[#063D56]/25 transition-all hover:scale-[1.02] hover:shadow-[#063D56]/40 active:scale-95">
          {msg.next}
        </button>

        <button
          onClick={handleBack}
          className="mt-2 w-full rounded-xl px-4 py-2 text-xs text-gray-400 hover:text-gray-600 transition-colors">
          ← Voltar para Dados Principais
        </button>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
